import { normalizeActionListResult } from '@spanel/shared-api-client';
import type { TaskClaimRewardResponse, TaskListItem } from '@spanel/shared-types';

import { parseLocalizedAmount } from '../utils/amount';

import { sendAction } from './api';

export type TaskProgressLine = {
  type: string;
  target: number;
  current: number;
  completed: boolean;
};

export type TaskItem = {
  taskKey: string;
  title: string;
  description: string;
  cta: string | null;
  claimable: boolean;
  status: 'active' | 'completed' | 'claimed';
  reward: { amount: number; expireDays: number };
  /** Koşul bazlı ilerleme (admin’deki görev tanımıyla uyumlu) */
  progressLines?: TaskProgressLine[];
};

export type TaskClaimResult = TaskClaimRewardResponse & {
  credits_added: number;
  available_credits: number;
};

function normalizeStatus(value: unknown): 'active' | 'completed' | 'claimed' | null {
  return value === 'active' || value === 'completed' || value === 'claimed' ? value : null;
}

function normalizeProgressLines(item: TaskListItem): TaskProgressLine[] | undefined {
  const raw = item.progress?.items;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const lines: TaskProgressLine[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const type = typeof (row as { type?: unknown }).type === 'string' ? String((row as { type: string }).type) : '';
    const target = parseLocalizedAmount((row as { target?: unknown }).target);
    const current = parseLocalizedAmount((row as { current?: unknown }).current);
    if (!type || target === null || target <= 0 || current === null || current < 0) continue;
    lines.push({
      type,
      target,
      current,
      completed: (row as { completed?: unknown }).completed === true,
    });
  }
  return lines.length ? lines : undefined;
}

function normalizeTask(item: TaskListItem): TaskItem | null {
  const rewardAmount = parseLocalizedAmount(item.reward?.amount);
  const status = normalizeStatus(item.status);
  if (rewardAmount === null || !Number.isInteger(rewardAmount) || rewardAmount <= 0 || !status) {
    return null;
  }

  const expireDays = parseLocalizedAmount(item.reward?.expire_days);
  return {
    taskKey: String(item.task_key || ''),
    title: String(item.title || ''),
    description: typeof item.description === 'string' ? item.description : '',
    cta: typeof item.cta === 'string' && item.cta.trim() ? item.cta.trim() : null,
    claimable: item.claimable === true,
    status,
    reward: {
      amount: rewardAmount,
      expireDays:
        expireDays !== null && Number.isInteger(expireDays) && expireDays >= 0 ? expireDays : 0,
    },
    progressLines: normalizeProgressLines(item),
  };
}

function normalizeClaimResponse(response: TaskClaimRewardResponse): TaskClaimResult {
  const creditsAdded = parseLocalizedAmount(response.credits_added);
  const availableCredits = parseLocalizedAmount(response.available_credits);
  if (
    creditsAdded === null ||
    !Number.isInteger(creditsAdded) ||
    creditsAdded < 0 ||
    availableCredits === null ||
    !Number.isInteger(availableCredits) ||
    availableCredits < 0
  ) {
    throw new Error('Görev ödül sonucu doğrulanamadı.');
  }
  return {
    ...response,
    credits_added: creditsAdded,
    available_credits: availableCredits,
  };
}

function normalizeListResponse(response: unknown): TaskItem[] {
  const { data } = normalizeActionListResult<TaskListItem>(response);
  const rows = Array.isArray(data) ? data : [];
  return rows
    .map(normalizeTask)
    .filter((task): task is TaskItem => Boolean(task?.taskKey && task.title));
}

export const tasksService = {
  list: async (): Promise<TaskItem[]> => {
    const response = await sendAction<unknown>('tasksList');
    return normalizeListResponse(response);
  },
  claim: async (taskKey: string): Promise<TaskClaimResult> => {
    const response = await sendAction<TaskClaimRewardResponse>('taskClaimReward', { task_key: taskKey });
    return normalizeClaimResponse(response);
  },
};
